import {
  CATEGORIAS_DE_CUMPLIMIENTO,
  conSeguimiento,
  ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO,
  ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO_EN,
  ORDEN_DE_SECCIONES,
  type CategoriaDeCumplimiento,
  type VinculoDeCumplimiento,
} from '@cnc/contracts';
import {useEffect, useState} from 'react';
import {useEditarVinculo, useQuitarVinculo, useUnirVinculos, useVinculosDeCumplimiento} from '../api/cumplimiento';
import {et, tr} from '../i18n/idioma';
import {numero} from '../utilidades/formato';
import {AYUDA_VINCULO as AV} from './ayuda-cumplimiento';
import {NotaDeAyuda} from './nota-de-ayuda';

const campo = 'mt-1 w-full rounded-md border border-linea bg-white px-2 py-1.5 text-sm';

// Editar un vinculo: nombre, categoria (p. ej. una apertura de cuenta que en
// realidad es un proveedor), carpeta principal, si es del exterior y unirlo
// con otro que es el mismo.
export function PanelDeVinculo({vinculo, onCerrar}: {vinculo: VinculoDeCumplimiento; onCerrar: () => void}) {
  const editar = useEditarVinculo();
  const unir = useUnirVinculos();
  const quitar = useQuitarVinculo();
  const todos = useVinculosDeCumplimiento();
  const categorias = et(ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO, ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO_EN);
  const [nombre, setNombre] = useState(vinculo.nombre);
  const [categoria, setCategoria] = useState<CategoriaDeCumplimiento>(vinculo.categoria);
  const [seccion, setSeccion] = useState(vinculo.seccion ?? '');
  const [internacional, setInternacional] = useState(vinculo.internacional);
  const [notas, setNotas] = useState(vinculo.notas ?? '');
  const [destino, setDestino] = useState('');
  useEffect(() => {
    const cerrar = (e: KeyboardEvent) => e.key === 'Escape' && onCerrar();
    window.addEventListener('keydown', cerrar);
    return () => window.removeEventListener('keydown', cerrar);
  }, [onCerrar]);
  const secciones = [
    ...new Set([...ORDEN_DE_SECCIONES, ...(todos.data?.vinculos ?? []).flatMap(x => (x.seccion ? [x.seccion] : []))]),
  ];
  const otros = (todos.data?.vinculos ?? [])
    .filter(x => x.id !== vinculo.id)
    .sort((a, b) => a.categoria.localeCompare(b.categoria) || a.nombre.localeCompare(b.nombre));
  const elegido = otros.find(x => String(x.id) === destino);
  const guardar = () =>
    editar.mutate(
      {
        id: vinculo.id,
        edicion: {
          version: vinculo.version,
          ...(nombre.trim() !== vinculo.nombre ? {nombre: nombre.trim()} : {}),
          ...(categoria !== vinculo.categoria ? {categoria} : {}),
          ...(seccion !== (vinculo.seccion ?? '') ? {seccion: seccion || null} : {}),
          ...(internacional !== vinculo.internacional ? {internacional} : {}),
          ...(notas !== (vinculo.notas ?? '') ? {notas: notas || null} : {}),
        },
      },
      {onSuccess: onCerrar},
    );
  return (
    <div
      className="fixed inset-0 z-[1100] flex justify-end bg-black/40"
      onClick={e => e.target === e.currentTarget && onCerrar()}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label={tr(`Editar ${vinculo.nombre}`, `Edit ${vinculo.nombre}`)}
        className="flex h-full w-full max-w-md flex-col gap-4 overflow-y-auto border-l border-linea bg-white p-5 shadow-2xl"
        data-prueba="panel-vinculo"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs tracking-wide text-apagado uppercase">{categorias[vinculo.categoria]}</p>
            <h2 className="text-lg font-semibold text-verde-oscuro">{vinculo.nombre}</h2>
            <p className="text-xs text-apagado">
              {tr(`${numero(vinculo.documentos)} archivos`, `${numero(vinculo.documentos)} files`)}
            </p>
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label={tr('Cerrar', 'Close')}
            className="rounded-md border border-linea px-2 py-1 text-sm hover:bg-papel"
          >
            ✕
          </button>
        </div>

        <form
          className="space-y-3"
          onSubmit={e => {
            e.preventDefault();
            guardar();
          }}
        >
          <label className="block text-xs text-apagado">
            {tr('Nombre', 'Name')}
            <NotaDeAyuda texto={AV.nombre} dentroDeEtiqueta />
            <input value={nombre} onChange={e => setNombre(e.target.value)} className={campo} />
          </label>
          <label className="block text-xs text-apagado">
            {tr('Categoría', 'Category')}
            <NotaDeAyuda texto={AV.categoria} dentroDeEtiqueta />
            <select
              value={categoria}
              onChange={e => setCategoria(e.target.value as CategoriaDeCumplimiento)}
              className={campo}
            >
              {CATEGORIAS_DE_CUMPLIMIENTO.filter(c => conSeguimiento(c) || c === vinculo.categoria).map(c => (
                <option key={c} value={c}>
                  {categorias[c]}
                </option>
              ))}
            </select>
            {categoria !== vinculo.categoria && (
              <span className="mt-1 block text-amber-900">
                {tr(
                  'Sus documentos se vuelven a reconocer con los tipos de la nueva categoría.',
                  'Its documents are recognized again with the types of the new category.',
                )}
              </span>
            )}
          </label>
          <label className="block text-xs text-apagado">
            {tr('Carpeta principal', 'Main folder')}
            <NotaDeAyuda texto={AV.seccion} dentroDeEtiqueta />
            <select value={seccion} onChange={e => setSeccion(e.target.value)} className={campo}>
              <option value="">{tr('Sin carpeta', 'No folder')}</option>
              {secciones.map(s => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={internacional} onChange={e => setInternacional(e.target.checked)} />
            {tr(
              'Empresa del exterior (se le piden los equivalentes: Tax ID, good standing, pasaporte…)',
              'Foreign company (equivalents are requested: Tax ID, good standing, passport…)',
            )}
            <NotaDeAyuda texto={AV.internacional} dentroDeEtiqueta />
          </label>
          <label className="block text-xs text-apagado">
            {tr('Notas', 'Notes')}
            <NotaDeAyuda texto={AV.notas} dentroDeEtiqueta />
            <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2} className={campo} />
          </label>
          <button
            type="submit"
            disabled={editar.isPending || !nombre.trim()}
            className="rounded-md bg-verde px-3 py-1.5 text-sm text-white hover:bg-verde-oscuro disabled:opacity-50"
          >
            {editar.isPending ? tr('Guardando…', 'Saving…') : tr('Guardar', 'Save')}
          </button>
          {editar.isError && <p className="text-xs text-alerta">{editar.error.message}</p>}
        </form>

        <div className="space-y-2 rounded-md border border-linea bg-papel p-3" data-prueba="quitar-vinculo">
          <h3 className="text-sm font-semibold">{tr('Quitar de esta pestaña', 'Remove from this tab')}</h3>
          {vinculo.documentos === 0 ? (
            <>
              <p className="text-xs text-apagado">
                {tr(
                  'No tiene archivos: se puede quitar (p. ej. una mina que no se sigue). Queda en la bitácora.',
                  'It has no files: it can be removed (e.g. a mine that is not tracked). It stays in the audit log.',
                )}
              </p>
              <button
                type="button"
                disabled={quitar.isPending}
                onClick={() => quitar.mutate(vinculo.id, {onSuccess: onCerrar})}
                className="rounded-md border border-alerta px-3 py-1.5 text-sm text-alerta hover:bg-red-50 disabled:opacity-50"
              >
                {tr(`Quitar "${vinculo.nombre}"`, `Remove "${vinculo.nombre}"`)}
              </button>
            </>
          ) : (
            <p className="text-xs text-apagado">
              {tr(
                'Tiene archivos: para sacarlo, cámbialo de categoría o únelo con otro vínculo.',
                'It has files: to take it out, change its category or merge it with another link.',
              )}
            </p>
          )}
          {quitar.isError && <p className="text-xs text-alerta">{quitar.error.message}</p>}
        </div>

        <div className="space-y-2 rounded-md border border-linea bg-papel p-3" data-prueba="unir-vinculo">
          <h3 className="text-sm font-semibold">
            {tr('Unir con otro vínculo', 'Merge with another link')}
            <NotaDeAyuda titulo={tr('Vínculo con el que se une', 'Link to merge with')} texto={AV.unir} />
          </h3>
          <p className="text-xs text-apagado">
            {tr(
              'Si es la misma empresa (p. ej. dos carpetas con nombres distintos), sus documentos pasan al vínculo elegido y este desaparece.',
              'If it is the same company (e.g. two folders with different names), its documents move to the chosen link and this one disappears.',
            )}
          </p>
          <select
            aria-label={tr('Vínculo con el que se une', 'Link to merge with')}
            value={destino}
            onChange={e => setDestino(e.target.value)}
            className={campo}
          >
            <option value="">{tr('Elige el vínculo…', 'Choose the link…')}</option>
            {otros.map(x => (
              <option key={x.id} value={x.id}>
                {categorias[x.categoria]} · {x.nombre}
              </option>
            ))}
          </select>
          {elegido && (
            <button
              type="button"
              disabled={unir.isPending}
              onClick={() => unir.mutate({origenId: vinculo.id, destinoId: elegido.id}, {onSuccess: onCerrar})}
              className="rounded-md border border-alerta px-3 py-1.5 text-sm text-alerta hover:bg-red-50 disabled:opacity-50"
            >
              {tr(
                `Unir "${vinculo.nombre}" con "${elegido.nombre}"`,
                `Merge "${vinculo.nombre}" into "${elegido.nombre}"`,
              )}
            </button>
          )}
          {unir.isError && <p className="text-xs text-alerta">{unir.error.message}</p>}
        </div>
      </section>
    </div>
  );
}
